"use client";

import { sortBy } from "lodash-es";
import ordinal from "ordinal";
import MarketResult from "@/components/headToHead/marketResult/MarketResult";
import OddsBtn, {
  useRedirectConfirmationModal,
} from "@/components/oddsBtn/OddsBtn";
import { NFL } from "@/constants/sports";
import RedirectToSportsbook from "@/events/Modals/RedirectToSportsbookModal";
import {
  EventMarketOdds,
  EventOddsResult,
  EventStatus,
  LineOddsSuccess,
  NflRecord,
  TeamLineOdds,
} from "@/graphql/gql/graphql";
import { useHeadToHeadContext } from "@/utils/hooks/useHeadToHeadContext";
import { formatHandicap, formatOdds } from "@/utils/scores";
import { TeamInfo } from "../../types";
import { formatNFLRecord } from "../headerUtil";
import styles from "./teamMatchupCard.module.scss";

interface TeamMatchupCardProps {
  team: TeamInfo;
  odds?: EventMarketOdds | null;
  oddsResult?: EventOddsResult | null;
  resultKey: "home" | "away";
  rawRecord?: NflRecord | null;
}

const OVER_INDEX = 0;
const UNDER_INDEX = 1;

export default function TeamMatchupCard({
  team,
  odds,
  oddsResult,
  resultKey,
  rawRecord,
}: TeamMatchupCardProps) {
  const { gameState } = useHeadToHeadContext();

  const { name, division, conference, numberfireId } = team ?? {};
  const { conferenceRank, divisionRank } = rawRecord ?? {};

  const teamName = name ?? (resultKey === "away" ? "Away" : "Home");
  const overOrUnderKey = resultKey === "away" ? "over" : "under";

  const record = formatNFLRecord(rawRecord);
  const conferencePlacement = `${ordinal(divisionRank ?? 0)} in ${division}`;
  const divisionPlacement = `${ordinal(conferenceRank ?? 0)} in ${conference}`;

  const result = oddsResult?.[resultKey];
  const overOrUnder = oddsResult?.[overOrUnderKey];

  const totalLetter = resultKey === "away" ? "O" : "U";
  const formattedLine =
    overOrUnder?.line != null ? `${totalLetter} ${overOrUnder?.line}` : "";

  return (
    <div className={styles.matchupCard}>
      <div className={styles.teamContent}>
        <div className={styles.teamName}>{teamName}</div>
        <div className={styles.teamRecord}>
          {record} | {conferencePlacement} | {divisionPlacement}
        </div>
        <div className={styles.marketHeadingsContainer}>
          <div className={styles.marketHeadings}>
            <span>Spread</span>
            <span>Moneyline</span>
            <span>Total</span>
          </div>
        </div>
        <div className={styles.marketsContainer}>
          {gameState === EventStatus.Final ? (
            <PostMarkets
              teamSuccess={result}
              ouSuccess={overOrUnder}
              ouFormattedLine={formattedLine}
            />
          ) : (
            <PreAndLiveMarkets
              teamNumberfireId={Number(numberfireId)}
              odds={odds}
              overUnderIndex={
                overOrUnderKey === "over" ? OVER_INDEX : UNDER_INDEX
              }
            />
          )}
        </div>
      </div>
    </div>
  );
}

const PreAndLiveMarkets = ({
  teamNumberfireId,
  odds,
  overUnderIndex,
}: {
  teamNumberfireId: number | null | undefined;
  odds: EventMarketOdds | null | undefined;
  overUnderIndex: number;
}) => {
  const { eventName } = useHeadToHeadContext();
  const { onOddsClick, redirectToSportsbookProps } =
    useRedirectConfirmationModal(NFL, eventName, "header");

  const { spread, moneyline, totalPoints } = odds ?? {};

  const moneylineForTeam = moneyline?.find(
    (item) => item?.teamNumberFireId === teamNumberfireId
  );
  const spreadForTeam = spread?.find(
    (item) => item?.teamNumberFireId === teamNumberfireId
  );
  const totals = sortBy(totalPoints, "formattedHandicap");
  const totalForTeam = totals[overUnderIndex];

  return (
    <>
      <RedirectToSportsbook {...redirectToSportsbookProps} />

      <OddsBtn
        classButton={styles.oddsBtn}
        classHandicap={styles.handicap}
        isSuspended={!spreadForTeam}
        handicap={spreadForTeam?.formattedHandicap ?? ""}
        odds={spreadForTeam?.odds ?? ""}
        onClick={() => onOddsClick(spreadForTeam?.url ?? "")}
      />

      <OddsBtn
        classButton={styles.oddsBtn}
        classHandicap={styles.handicap}
        isSuspended={!moneylineForTeam}
        odds={moneylineForTeam?.odds ?? ""}
        onClick={() => onOddsClick(moneylineForTeam?.url ?? "")}
      />

      <OddsBtn
        classButton={styles.oddsBtn}
        classHandicap={styles.handicap}
        isSuspended={!totalForTeam}
        handicap={totalForTeam?.formattedHandicap ?? ""}
        odds={totalForTeam?.odds ?? ""}
        onClick={() => onOddsClick(totalForTeam?.url ?? "")}
      />
    </>
  );
};

interface PostMarketsProps {
  teamSuccess?: TeamLineOdds | null;
  ouSuccess?: LineOddsSuccess | null;
  ouFormattedLine?: string | null;
}
const PostMarkets = ({
  teamSuccess,
  ouSuccess,
  ouFormattedLine,
}: PostMarketsProps) => {
  return (
    <>
      <MarketResult
        handicap={formatHandicap(teamSuccess?.spread?.line)}
        odds={formatOdds(teamSuccess?.spread?.odds)}
        hit={teamSuccess?.spread?.success}
      />
      <MarketResult
        odds={formatOdds(teamSuccess?.moneyline?.odds)}
        hit={teamSuccess?.moneyline?.success}
      />
      <MarketResult
        handicap={ouFormattedLine}
        odds={formatOdds(ouSuccess?.odds)}
        hit={ouSuccess?.success}
      />
    </>
  );
};
